import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent
} from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../auth/auth.service';
import { AppConfig } from '../../../app.config';
import {catchError} from 'rxjs/operators';
// tslint:disable-next-line:import-blacklist
import {throwError} from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService: AuthService;

  constructor(
    private router: Router,
    private injector: Injector,
    private toastr: ToastrService
  ) {
    this.authService = injector.get(AuthService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler):
    Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    return next.handle(this.transformRequest(req)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            if (this.isTokenRequest(req)) {
              // if refresh token url has errored, then refresh token is invalid, and they must re-login
              return this.logoutAndRedirect(err);
            } else if (req && req.url.includes('auth') && req.method === 'POST') {
              // if 401 on the login page, it's because they failed to log in - this gets handled in the login component so just rethrow
              return throwError(err);
            } else if (!this.authService.isAuthenticated) {
              return this.logoutAndRedirect(err);
            } else {
              return this.handleUnauthorised(req, next, err);
            }
          } else if (err.status === 403) {
            return this.logoutAndRedirect(err);
          } else if (this.isApiRequest(req) && err.status >= 400 && err.status < 500 && !this.authService.isAuthenticated) {
            return this.logoutAndRedirect(err);
          } else if (err.status === 500) {
            this.toastr.warning('Internal Server Error - contact a system administrator for assistance.', 'Something went wrong');
            return throwError(err);
          } else {
            return throwError(err);
          }
        }

        return throwError(err);
      }));
  }

  private logoutAndRedirect(err: any)
    : Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    this.authService.logout();
    this.router.navigateByUrl(AppConfig.settings.auth.loginRoute);
    return throwError(err);
  }

  private handleUnauthorised(req: HttpRequest<any>, next: HttpHandler, originalError: any) {
    const originalAuthHeader = req.headers.get('Authorization') || this.authService.getAuthHeader();

    // Refresh the token and replay the failed request before allowing a login redirect.
    return this.authService
      .refreshAccessToken()
      .catch(() => this.waitForAuthHeaderChange(originalAuthHeader))
      .mergeMap(token => next.handle(this.retryRequestWithToken(req, token)))
      .catch(() => this.logoutAndRedirect(originalError));
  }

  private waitForAuthHeaderChange(originalAuthHeader: string): Observable<string> {
    return Observable.create(observer => {
      let completed = false;
      // Hidden iframe refresh can update localStorage after the MSAL Observable has errored.
      const interval = setInterval(() => {
        const currentAuthHeader = this.authService.getAuthHeader();

        if (!currentAuthHeader || currentAuthHeader === originalAuthHeader) {
          return;
        }

        completed = true;
        clearInterval(interval);
        clearTimeout(timeout);
        observer.next(currentAuthHeader.replace(/^Bearer\s+/i, ''));
        observer.complete();
      }, 250);

      const timeout = setTimeout(() => {
        if (completed) {
          return;
        }

        clearInterval(interval);
        observer.error('Timed out waiting for auth header refresh.');
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    });
  }

  private transformRequest(request: HttpRequest<any>, force: boolean = false) {
    request = this.addAuthHeaders(request, force);

    return request;
  }

  private retryRequestWithToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (!this.isApiRequest(request) || AppConfig.settings.auth.useWindowsAuthentication) {
      return request;
    }

    // Use the token returned by refresh rather than relying on localStorage timing.
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
  }

  private addAuthHeaders(request: HttpRequest<any>, force: boolean = false): HttpRequest<any> {
    // If it's not being sent to our endpoint, don't add the auth headers
    if (!this.isApiRequest(request)) {
      return request;
    }

    if (request.headers.has('Authorization') && !force) {
      return request;
    } else if (AppConfig.settings.auth.useWindowsAuthentication) {
      return request.clone({ withCredentials: true });
    } else {
      const authHeader = this.authService.getAuthHeader();
      return authHeader
        ? request.clone({ setHeaders: { Authorization: authHeader }, withCredentials: true })
        : request.clone({ withCredentials: true });
    }
  }

  private isTokenRequest(req: HttpRequest<any>): boolean {
    if (!req) {
      return false;
    }

    const tokenUrl = `${AppConfig.settings.baseApiUrl}${AppConfig.settings.auth.authTokenUrl}`;
    return req.url.indexOf(tokenUrl) >= 0 || req.url.indexOf(AppConfig.settings.auth.authTokenUrl) >= 0;
  }

  private isApiRequest(req: HttpRequest<any>): boolean {
    return !!req && req.url.indexOf(AppConfig.settings.apiUrl) >= 0;
  }
}
